import { DEFAULT_MODE, DEFAULT_PALETTE_CONFIG } from "./helpers/constants";
import {
    HSLToHex,
    clamp,
    hexToHSL,
    lightnessFromHSLum,
    luminanceFromHex,
    unsignedModulo,
    getValueStopFromLightness,
} from "./helpers/converters";
import {
    createDistributionValues,
    createHueScale,
    createSaturationScale,
} from "./helpers/scales";
import { Palette, Swatch } from "./types";

export function generateColorPalette(color: string): Record<number, string> {
    const palette = createPaletteFromNameValue("primary", color);
    return palette.swatches.reduce((acc: Record<number, string>, s: Swatch) => {
        // if (s.stop === 0) {
        //     acc[s.stop] = "#ffffff";
        // } else if (s.stop === 1000) {
        //     acc[s.stop] = "#000000";
        // } else {
        //     acc[s.stop] = s.hex;
        // }
        acc[s.stop] = s.hex;

        return acc;
    }, {});
}

export function createPaletteFromNameValue(
    name: string,
    value: string // hex color
): Palette {
    const valueStop = getValueStopFromLightness(value);

    const nameValue: Palette = {
        id: name,
        name: name,
        value: value.toUpperCase(),
        valueStop: valueStop,
        swatches: [],
        h: 0,
        s: 0,
        lMin: 0,
        lMax: 100,
        mode: DEFAULT_MODE,
    };

    return {
        ...nameValue,
        swatches: createSwatches(nameValue),
    };
}

function createSwatches(palette: Palette): Swatch[] {
    const { value, valueStop } = palette;

    const useLightness =
        palette.useLightness ?? DEFAULT_PALETTE_CONFIG.useLightness;
    const h = palette.h ?? DEFAULT_PALETTE_CONFIG.h;
    const s = palette.s ?? DEFAULT_PALETTE_CONFIG.s;
    const lMin = palette.lMin ?? DEFAULT_PALETTE_CONFIG.lMin;
    const lMax = palette.lMax ?? DEFAULT_PALETTE_CONFIG.lMax;

    const hueScale = createHueScale(h, valueStop);
    const saturationScale = createSaturationScale(s, valueStop);

    const { h: valueH, s: valueS, l: valueL } = hexToHSL(value);

    const lightnessValue = useLightness ? valueL : luminanceFromHex(value);
    const distributionScale = createDistributionValues(
        lMin,
        lMax,
        lightnessValue,
        valueStop
    );

    const swatches = hueScale.map(({ stop }, stopIndex) => {
        const newH = unsignedModulo(valueH + hueScale[stopIndex].tweak, 360);
        const newS = clamp(valueS + saturationScale[stopIndex].tweak, 0, 100);
        const newL = useLightness
            ? distributionScale[stopIndex].tweak
            : lightnessFromHSLum(
                  newH,
                  newS,
                  distributionScale[stopIndex].tweak
              );

        const newHex = HSLToHex(newH, newS, newL);

        return {
            stop,
            hex:
                stop === valueStop
                    ? value.startsWith("#")
                        ? value.toUpperCase()
                        : `#${value.toUpperCase()}`
                    : newHex.toUpperCase(),
            h: newH,
            hScale:
                ((unsignedModulo(hueScale[stopIndex].tweak + 180, 360) - 180) /
                    180) *
                50,
            s: newS,
            sScale: newS - 50,
            l: newL,
        };
    });

    return swatches;
}
